import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit, ElementRef, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";

import { IUserModel, IUserResponse, NewUserRequestDataModel, SettingsUserModel } from "../../settings-interfaces";
import {
    AnalyticsService,
    BroadcastService,
    CardViewService,
    FirmsService,
    HelpersService,
    ResponseErrorHandlerService,
    UsersService,
} from "../../shared";
import { IframeMessage, IframeMessagesService } from "../../shared/iframe-messages-service/iframe-messages.service";
import { AuthService } from "../../shared/auth/auth.service";
import { UserSettingsIframeHeightService } from "../../shared/user-settings-iframe-height-service/user-settings-iframe-height.service";

@Component({
    selector: "wk-settings-user-edit-manager",
    templateUrl: "./settings-user-edit-manager.component.html",
    styleUrl: "./settings-user-edit-manager.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    host: { class: "wk-suem" },
})
export class SettingsUserEditManagerComponent implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly usersService = inject(UsersService);
    private readonly cardViewService = inject(CardViewService);
    private readonly firmsService = inject(FirmsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly authService = inject(AuthService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    subscription: Subscription;
    subscriptions: Subscription[] = [];
    userId: string | null = null;
    userModel: IUserModel;
    initUserModel: string;
    LastDashboardId = "";
    isSaving = 0;
    formClicked = false;
    currentUser = false;
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    page: number = 1;
    search: string;
    private iframeHeightService = inject(UserSettingsIframeHeightService);
    private elRef = inject(ElementRef);

    constructor() {
        this.registerIframeMessagesBroadcast();
    }

    async ngOnInit() {
        this.iframeHeightService.init(this.elRef.nativeElement);
        if (this.authService.loggedIn()) {
            await this.buildModel();
            this.changeDetectorRef.detectChanges();
        }

        this.route.params.subscribe(async () => {
            if (this.authService.loggedIn()) {
                await this.buildModel();
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    registerIframeMessagesBroadcast() {
        this.subscriptions.push(
            BroadcastService.on<IframeMessage>("taxcanDashboardUserSettings:save").subscribe((message) => {
                let sendUserSaved = true;
                if (/^[a-zA-Z\u00C0-\u00FF0-9._%+-]+@[a-zA-Z\u00C0-\u00FF0-9.-]+\.[a-zA-Z]{2,63}$/.test(message.data.EmailAddress)) {
                    if (this.userId !== "new" || message.data.LicenseEnabled) {
                        if (!this.currentUser && message.data.LicenseEnabled !== null) {
                            this.userModel.Active = this.userModel.Active && message.data.LicenseEnabled;
                        }
                        this.userModel.email = message.data.EmailAddress?.toLowerCase();
                        this.userModel.firstName = message.data.FirstName;
                        this.userModel.lastName = message.data.LastName;
                        this.userModel.IFirmUserId = message.data.UserId;
                        sendUserSaved = false;
                        this.saveUser();
                    }
                }
                if (sendUserSaved) {
                    this.iframeMessagesService.postMessage("taxcanDashboardUserSettings:saveDone", null);
                }
            }),
        );
    }

    saveUser() {
        if (this.isSaving) {
            return false;
        }
        this.formClicked = true;

        this.cardViewService.settingsSectionChanged = true;

        this.startSaveAction();
        if (this.userId === "new") {
            const newUser = new NewUserRequestDataModel();
            newUser.Email = this.userModel.email;
            newUser.IFirmUserId = this.userModel.IFirmUserId;
            newUser.FirstName = this.userModel.firstName;
            newUser.LastName = this.userModel.lastName;
            newUser.CommunicationLanguage = this.userModel.CommunicationLanguage;
            newUser.LastDashboardId = this.LastDashboardId; // this parameter should be in POST
            newUser.IsAdmin = this.userModel.IsAdmin;
            newUser.IsSupport = false;

            this.usersService.addUser(newUser).subscribe(
                (userData: IUserResponse) => {
                    if (userData && userData.User && userData.User.Id) {
                        this.userId = userData.User.Id;
                        this.sendAnalitics("User created");

                        this.responseErrorHandlerService.showPopup({
                            typeLayout: "SUCCESS",
                            messageCode: "successfullySaved",
                        });
                        // saving into MergedUserData
                        this.usersService.MergedUserData[userData.User.Id] = userData.User;
                        this.usersService.MergedUserData[userData.User.Id].Id = userData.User.Id;
                        this.usersService.MergedUserData[userData.User.Id].Active = true;
                        this.usersService.MergedUserData[userData.User.Id].UserDashboardSettings = userData.User.UserDashboardSettings;
                        this.initUserModel = "onBackFunctionCalled";
                        this.endSaveAction();
                    }
                },
                () => {
                    this.endSaveAction();
                },
            );
        } else {
            const userNewInfo = new Object();
            userNewInfo["Active"] = this.userModel.Active;
            userNewInfo["Email"] = this.userModel.email;
            userNewInfo["IFirmUserId"] = this.userModel.IFirmUserId?.toString();
            userNewInfo["FirstName"] = this.userModel.firstName;
            userNewInfo["LastName"] = this.userModel.lastName;
            userNewInfo["IsAdmin"] = this.userModel.IsAdmin;
            userNewInfo["CommunicationLanguage"] = this.userModel.CommunicationLanguage;
            userNewInfo["IsSupport"] = false;
            userNewInfo["LastDashboardId"] = this.LastDashboardId;

            this.usersService.updateUser(this.userId, userNewInfo).subscribe(
                (userData) => {
                    this.sendAnalitics("User changed");
                    // saving into MergedUserData
                    this.usersService.MergedUserData[this.userId] = userNewInfo;
                    this.usersService.MergedUserData[this.userId].Id = this.userId;
                    this.usersService.MergedUserData[this.userId].Active = true;
                    this.usersService.MergedUserData[this.userId].UserDashboardSettings = [];
                    // saving into MergedUserData
                    if (this.isSaving === 0) {
                        this.responseErrorHandlerService.showPopup({
                            typeLayout: "SUCCESS",
                            messageCode: "successfullySaved",
                        });
                    }
                    this.initUserModel = "onBackFunctionCalled";
                    this.endSaveAction();
                },
                () => {
                    this.initUserModel = "onBackFunctionCalled";
                    this.endSaveAction();
                },
            );
        }
    }

    disableUser(): void {
        if (this.userId !== "new") {
            this.cardViewService.settingsSectionChanged = true;
            this.sendAnalitics("User disabled");
            this.userModel.Active = false;
            this.usersService.MergedUserData[this.userId].Active = false;
        }
    }

    enableUser(): void {
        if (this.userId !== "new") {
            this.cardViewService.settingsSectionChanged = true;
            this.sendAnalitics("User enabled");
            this.userModel.Active = true;
            this.usersService.MergedUserData[this.userId].Active = true;
        }
    }

    async buildModel(keepUserID = false): Promise<void> {
        if (!keepUserID) this.userId = null;
        if (!this.helpersService.userDataMerged) {
            await this.helpersService.mergeUserData();
        }

        const userId = keepUserID ? this.userId : this.route.snapshot.params["userId"];
        if (userId !== "new") {
            const user = this.usersService.MergedUserData[userId];
            if (user && user.Id) {
                this.userModel = new SettingsUserModel();
                this.userModel.Active = user.Active;
                this.userModel.userId = user.Id;
                this.userModel.firstName = user.FirstName;
                this.userModel.lastName = user.LastName;
                this.userModel.email = user.Email;
                this.userModel.IFirmUserId = user.IFirmUserId;
                this.userModel.IsAdmin = user.IsAdmin;
                this.userModel.CommunicationLanguage = user.CommunicationLanguage;
                this.LastDashboardId = user.LastDashboardId;
                this.userId = userId;
                this.currentUser = this.authService.currentUser?.sub === userId;
            }
        } else {
            this.userModel = new SettingsUserModel();
            this.userId = userId;
            this.userModel.CommunicationLanguage = (await this.firmsService.getFirmById(this.authService.currentUser.firm_id)).DefaultLanguage;
        }
        this.initUserModel = JSON.stringify(this.userModel);
        this.helpersService.suppressSpinner();
    }

    sendAnalitics(message) {
        if (message === "User disabled") {
            this.analyticsService.track(message);
        }
    }

    startSaveAction() {
        this.isSaving++;
    }

    endSaveAction() {
        this.isSaving--;
        if (this.isSaving == 0) {
            this.iframeMessagesService.postMessage("taxcanDashboardUserSettings:saveDone", null);
        }
    }
}
